import { useState } from 'react';
import SmartLink from './SmartLink';
import { FaFacebookF, FaInstagram } from 'react-icons/fa';
import { HiOutlineMail, HiOutlinePhone } from 'react-icons/hi';
import Logo from './Logo';
import LegalModal from './LegalModal';
import { privacyPolicy, termsOfService } from '../data/legalContent';
import { CONTACT_EMAIL, CONTACT_PHONE_DISPLAY, SOCIAL_LINKS } from '../data/contactInfo';

const socialIcons = {
  Instagram: FaInstagram,
  Facebook: FaFacebookF,
};

const leftQuickLinks = [
  { to: '/', label: 'Home' },
  { to: '/shop', label: 'Shop' },
];

const rightQuickLinks = [
  { to: '/about', label: 'About Us' },
  { to: '/return-policy', label: 'Return Policy' },
];

const Footer = () => {
  const [legalModal, setLegalModal] = useState(null);
  const closeLegal = () => setLegalModal(null);

  return (
    <>
      <footer
        data-nav-bg="#1C1612"
        className="bg-lumora-dark-surface border-t border-lumora-cream/10 relative z-10"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 pt-12 pb-8 md:pt-16 md:pb-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-12">
            {/* Brand */}
            <div className="lg:col-span-5 flex flex-col items-center text-center md:items-start md:text-left">
              <Logo size="lg" className="mb-5" />
              <p className="text-body-dark text-sm leading-relaxed max-w-sm md:max-w-xs">
                Handcrafted wooden lamps designed to bring warmth, elegance, and timeless beauty into
                your home.
              </p>
              <div className="flex gap-3 mt-6 justify-center md:justify-start">
                {SOCIAL_LINKS.map(({ label, href }) => {
                  const Icon = socialIcons[label];
                  return (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full border border-lumora-cream/15 flex items-center justify-center text-lumora-muted hover:border-lumora-gold/40 hover:text-lumora-gold hover:shadow-glow transition-all duration-300"
                      aria-label={label}
                    >
                      <Icon className="w-4 h-4" />
                    </a>
                  );
                })}
              </div>
            </div>

            {/* Quick Links */}
            <div className="lg:col-span-3 flex flex-col items-center md:items-start text-center md:text-left border-t border-lumora-cream/10 pt-8 md:border-0 md:pt-0">
              <h4 className="font-serif text-base md:text-lg text-lumora-gold mb-5 tracking-wide">
                Quick Links
              </h4>
              <div className="flex gap-x-10 gap-y-4 mx-auto md:mx-0">
                <div className="flex flex-col gap-4 items-center md:items-start">
                  {leftQuickLinks.map((link) => (
                    <SmartLink
                      key={link.to}
                      to={link.to}
                      className="text-sm text-lumora-muted hover:text-lumora-gold transition-colors whitespace-nowrap text-center md:text-left"
                    >
                      {link.label}
                    </SmartLink>
                  ))}
                </div>
                <div className="flex flex-col gap-4 items-center md:items-start">
                  {rightQuickLinks.map((link) => (
                    <SmartLink
                      key={link.to}
                      to={link.to}
                      className="text-sm text-lumora-muted hover:text-lumora-gold transition-colors whitespace-nowrap text-center md:text-left"
                    >
                      {link.label}
                    </SmartLink>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact info */}
            <div className="lg:col-span-4 flex flex-col items-center md:items-start border-t border-lumora-cream/10 pt-8 md:border-0 md:pt-0 lg:pl-4">
              <h4 className="font-serif text-base md:text-lg text-lumora-gold mb-5 tracking-wide text-center md:text-left w-full">
                Contact
              </h4>
              <ul className="w-full max-w-[280px] space-y-4 mx-auto md:mx-0">
                <li>
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="grid grid-cols-[2.25rem_1fr] items-center gap-3 text-sm text-lumora-muted hover:text-lumora-gold transition-colors group"
                  >
                    <span className="w-9 h-9 rounded-full border border-lumora-cream/10 flex items-center justify-center group-hover:border-lumora-gold/30 transition-colors">
                      <HiOutlineMail className="w-4 h-4 text-lumora-gold" />
                    </span>
                    <span className="text-left break-all sm:break-normal">{CONTACT_EMAIL}</span>
                  </a>
                </li>
                <li>
                  <a
                    href={`tel:${CONTACT_PHONE_DISPLAY.replace(/\s/g, '')}`}
                    className="grid grid-cols-[2.25rem_1fr] items-center gap-3 text-sm text-lumora-muted hover:text-lumora-gold transition-colors group"
                  >
                    <span className="w-9 h-9 rounded-full border border-lumora-cream/10 flex items-center justify-center group-hover:border-lumora-gold/30 transition-colors">
                      <HiOutlinePhone className="w-4 h-4 text-lumora-gold" />
                    </span>
                    <span className="text-left">{CONTACT_PHONE_DISPLAY}</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-10 md:mt-14 pt-6 md:pt-8 border-t border-lumora-cream/10 flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center items-center gap-4 text-xs sm:text-sm text-lumora-muted">
            <p className="w-full text-center sm:w-auto sm:text-left">
              &copy; {new Date().getFullYear()} Lumora. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center sm:justify-end gap-5 sm:gap-6 w-full sm:w-auto">
              <SmartLink to="/return-policy" className="hover:text-lumora-gold transition-colors">
                Return Policy
              </SmartLink>
              <button
                type="button"
                onClick={() => setLegalModal('privacy')}
                className="hover:text-lumora-gold transition-colors"
              >
                Privacy Policy
              </button>
              <button
                type="button"
                onClick={() => setLegalModal('terms')}
                className="hover:text-lumora-gold transition-colors"
              >
                Terms of Service
              </button>
            </div>
          </div>
        </div>
      </footer>

      <LegalModal
        isOpen={legalModal === 'privacy'}
        onClose={closeLegal}
        title={privacyPolicy.title}
        updated={privacyPolicy.updated}
        sections={privacyPolicy.sections}
      />
      <LegalModal
        isOpen={legalModal === 'terms'}
        onClose={closeLegal}
        title={termsOfService.title}
        updated={termsOfService.updated}
        sections={termsOfService.sections}
      />
    </>
  );
};

export default Footer;
